"use client";

import { useEffect } from "react";
import { platformFetch } from "./browser";
import { PlatformError } from "./client";

/* Inbound referral links.

   A referred player arrives with a code in the URL — `/?referral_code=X`, the
   short `/?ref=X`, or the whitelabel-shaped `/join?referral_code=X` and
   `/?referral=X` — and the platform credits the referrer only if that code
   rides along on the sign-in that follows: every sign-in (magic link,
   SIWE/SIWS, Telegram, and the Google and X authorize calls) takes an optional
   `referral_code`.

   An affiliate click is the same parameter with the affiliate platform named
   beside it, as the whitelabel's links carry it: `?referral=<click id>&ap=scaleo
   &affiliate_id=<n>`. That one is sent as `referral_code` plus `source: "scaleo"`
   and `affiliate_id`. A click id is not a platform referral code, so it is not
   checked against `/referral/{code}/validate` — the platform answers a click id
   another account already claimed with a 409 `REFERRAL_CODE_CONFLICT`, and the
   sign-in is then retried without it (`sendWithReferral`), as the whitelabel
   does.

   The code is captured once at landing and kept in sessionStorage, because the
   player browses before signing in and the query string does not survive the
   first navigation. A plain code is validated before it is kept: an invalid
   code silently attached to a sign-in would be refused at the worst moment,
   after the player has done everything right.

   sessionStorage rather than localStorage on purpose — a referral attributes
   the visit that carried it, not every visit from this browser forever. */

const STORAGE_KEY = "referral-code";

/* Enough for every code the platform issues, tight enough that nothing
   URL-shaped or script-shaped gets stored and replayed into a request body. */
const CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/* The platform's affiliate id is a positive int4. */
const AFFILIATE_ID_MAX = 2147483647;

export type Referral = {
  code: string;
  source?: "scaleo";
  affiliateId?: number;
};

/* The body (or query) fields a sign-in carries for a referral. */
export type ReferralFields = {
  referral_code?: string;
  source?: "scaleo";
  affiliate_id?: number;
};

const affiliateIdFrom = (value: string | null): number | undefined => {
  if (value === null || !/^\d{1,10}$/.test(value)) {
    return undefined;
  }

  const id = Number(value);

  return id >= 1 && id <= AFFILIATE_ID_MAX ? id : undefined;
};

export const referralFrom = (search: string): Referral | null => {
  const params = new URLSearchParams(search);
  const code = params.get("referral_code") ?? params.get("ref") ?? params.get("referral");

  if (code === null || !CODE_PATTERN.test(code)) {
    return null;
  }

  const platform = params.get("ap");

  if (platform === null) {
    return { code };
  }

  /* An affiliate platform the site does not know is dropped whole, as the
     whitelabel drops it: sending its click id as a plain code would credit
     nobody and might collide with a real one. */
  if (platform !== "scaleo") {
    return null;
  }

  const affiliateId = affiliateIdFrom(params.get("affiliate_id"));

  return affiliateId === undefined
    ? { code, source: "scaleo" }
    : { code, source: "scaleo", affiliateId };
};

export const referralCodeFrom = (search: string): string | null => referralFrom(search)?.code ?? null;

/* What was stored, read back defensively: a value written before referrals
   carried a source is a bare code. */
export const parseStoredReferral = (raw: string | null): Referral | null => {
  if (raw === null || raw === "") {
    return null;
  }

  if (CODE_PATTERN.test(raw)) {
    return { code: raw };
  }

  try {
    const value = JSON.parse(raw) as Partial<Referral>;

    if (typeof value.code !== "string" || !CODE_PATTERN.test(value.code)) {
      return null;
    }

    if (value.source !== "scaleo") {
      return { code: value.code };
    }

    const affiliateId =
      typeof value.affiliateId === "number" ? affiliateIdFrom(String(value.affiliateId)) : undefined;

    return affiliateId === undefined
      ? { code: value.code, source: "scaleo" }
      : { code: value.code, source: "scaleo", affiliateId };
  } catch {
    return null;
  }
};

export const storedReferral = (): Referral | null => {
  try {
    return parseStoredReferral(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
};

export const storedReferralCode = (): string | null => storedReferral()?.code ?? null;

const storeReferral = (referral: Referral) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(referral));
  } catch {
    /* No storage, no attribution — the sign-in itself is unaffected. */
  }
};

export const clearReferralCode = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Nothing stored means nothing to clear. */
  }
};

export const referralFields = (referral: Referral | null): ReferralFields => {
  if (referral === null) {
    return {};
  }

  return {
    referral_code: referral.code,
    ...(referral.source ? { source: referral.source } : {}),
    ...(referral.affiliateId !== undefined ? { affiliate_id: referral.affiliateId } : {}),
  };
};

/* The platform's answer to a click id another account already claimed. */
export const isReferralCodeConflict = (error: unknown) =>
  error instanceof PlatformError &&
  error.status === 409 &&
  typeof error.details === "object" &&
  error.details !== null &&
  (error.details as { code?: unknown }).code === "REFERRAL_CODE_CONFLICT";

/* One sign-in call with the referral attached, and — on a conflict over that
   referral — once more without it. The referral is dropped either way once the
   platform has answered for it: attached to a sign-in that worked, or refused
   for good. Any other failure keeps it, so a retry of the sign-in still
   carries it. */
export const sendWithReferral = async <T>(
  referral: Referral | null,
  send: (fields: ReferralFields) => Promise<T>,
  drop: () => void,
): Promise<T> => {
  try {
    const result = await send(referralFields(referral));
    drop();
    return result;
  } catch (error) {
    if (referral === null || !isReferralCodeConflict(error)) {
      throw error;
    }

    drop();
    return send({});
  }
};

/* The same, against the referral this browser holds. */
export const signInWithReferral = <T>(send: (fields: ReferralFields) => Promise<T>) =>
  sendWithReferral(storedReferral(), send, clearReferralCode);

/* Mounted once in the shell, so a code is caught on whichever page the link
   landed on. Validation failing open would store junk and validation failing
   closed would lose real referrals on a flaky connection — this fails closed,
   because a lost attribution is recoverable support work and a refused sign-in
   is a lost player. */
export const useReferralCapture = () => {
  useEffect(() => {
    const referral = referralFrom(window.location.search);
    const held = storedReferral();

    if (referral === null || (held?.code === referral.code && held.source === referral.source)) {
      return;
    }

    /* A click id is the affiliate platform's, not ours to validate. */
    if (referral.source === "scaleo") {
      storeReferral(referral);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const { data } = await platformFetch<{ data: { isValid: boolean } }>(
          `referral/${referral.code}/validate`,
        );

        if (!cancelled && data.isValid) {
          storeReferral(referral);
        }
      } catch {
        /* Unknown code or unreachable API — nothing is stored either way. */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);
};
